import React, {
  useEffect,
  useState,
  useRef,
  memo,
  useCallback,
  KeyboardEvent,
  forwardRef,
  useImperativeHandle,
  Component,
  createRef,
  RefObject,
} from "react";

import type { CustomCellEditorProps } from "ag-grid-react";
import {
  IHeaderParams,
  ICellEditorParams,
  ICellEditorComp,
} from "ag-grid-community";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { Checkbox } from "@mui/material";
import { CustomColDef } from "./SnGridTypes";
import { PatternFormat } from "react-number-format";
import { TextField } from "@mui/material";
import { showDialog } from "@widgets/CommonDialog";

export const DateBoxCellEditor = memo(
  ({
    value,
    onValueChange,
    stopEditing,
    data,
    column,
  }: CustomCellEditorProps) => {
    const [open, setOpen] = useState(false); // 달력 열림 상태
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [internalValue, setInternalValue] = useState<Dayjs | null>(
      value ? dayjs(value) : null
    );
    const disabled = useRef<boolean>(false);
    const handleClick = () => {
      if (!disabled.current) setOpen(true); // 클릭했을 때만 달력 오픈
    };
    useEffect(() => {
      const colDef = column.getColDef() as any;

      if (colDef.columnConfig) {
        let cond = colDef.columnConfig.dc_editcon;
        if (cond == null || cond == "") {
          setOpen(true);
          return;
        }
        if (data.state === "insert") {
          cond = cond.replace("new", "false");
        } else {
          cond = cond.replace("new", "true");
        }
        Object.keys(data)
          .sort((a, b) => b.length - a.length)
          .forEach((key) => {
            cond = cond.replaceAll(key, data[key]);
          });
        const editable = !new Function(`return (${cond})`)();
        if (!editable) {
          stopEditing();
          return;
        }
      }
      const input = wrapperRef.current?.querySelector(
        "input"
      ) as HTMLInputElement | null;
      if (!input) return;

      input.focus();
      setOpen(true);
    }, []);

    const handleChange = (newValue: Dayjs | null) => {
      setInternalValue(newValue);
      onValueChange(newValue ? newValue.format("YYYYMMDD") : "");
    };
    const handleClose = () => {
      setOpen(false); // 날짜 선택 없이도 닫힘
      stopEditing();
    };

    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <div ref={wrapperRef}>
          <DatePicker
            format="YYYY/MM/DD"
            // value={value ? dayjs(value) : null}
            value={internalValue}
            onError={() => {}}
            open={open}
            onClose={handleClose}
            onChange={handleChange}
            disabled={disabled.current}
            slotProps={{
              textField: {
                error: false, // 에러 상태 비활성화
                helperText: "", // 헬퍼 텍스트 제거
                InputProps: {
                  endAdornment: null, // 달력 아이콘 제거
                },
                onClick: handleClick, // 클릭 시 달력 열기
              },
            }}
            sx={{
              width: "100%",
              "& .MuiInputBase-root": {
                height: "32px", // ag-grid 기본 rowHeight 고려
                fontSize: "12px",
                padding: 0,
                alignItems: "center", // 세로 정렬
                display: "flex",
              },
              "& .MuiInputBase-input": {
                padding: "0 8px",
                lineHeight: "normal",
                fontSize: "12px",
              },
              "& .MuiOutlinedInput-root": {
                border: "none", // 테두리 제거
                padding: 0,
              },
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none", // 기본 테두리 제거
              },
              "& .MuiSvgIcon-root": {
                display: "none", // 달력 아이콘 숨기기
              },
            }}
          />
        </div>
      </LocalizationProvider>
    );
  }
);

export const CustomHeaderCheckbox = memo((props: IHeaderParams) => {
  const [checked, setChecked] = useState<boolean>(false);

  const updateHeaderCheckboxState = useCallback(() => {
    let totalFiltered = 0;
    let checkedCount = 0;

    props.api.forEachNodeAfterFilter((node) => {
      totalFiltered++;
      if (node.data?.fixCheckbox === true) {
        checkedCount++;
      }
    });

    // 모든 필터링된 row가 체크된 경우에만 true
    setChecked(totalFiltered > 0 && checkedCount === totalFiltered);
  }, [props.api]);

  const handleToggle = (next: boolean) => {
    setChecked(next);

    const updatedRows: any[] = [];
    props.api.forEachNodeAfterFilter((node) => {
      updatedRows.push({
        ...node.data,
        fixCheckbox: next,
      });
    });

    props.api.applyTransaction({
      update: updatedRows,
    });

    props.context?.onCheckedRowChaged?.(next, "header");
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setChecked(isChecked);
    const updatedRows: any[] = [];
    props.api.forEachNodeAfterFilter((node) => {
      updatedRows.push({
        ...node.data,
        fixCheckbox: isChecked,
      });
    });
    props.api.applyTransaction({
      update: updatedRows,
    });
    props.context?.onCheckedRowChaged?.(isChecked, "header");
  };

  // 최초 mount 및 row 변경시 상태 동기화
  useEffect(() => {
    updateHeaderCheckboxState();

    const onCellValueChanged = () => updateHeaderCheckboxState();
    const onFilterChanged = () => updateHeaderCheckboxState();

    props.api.addEventListener("cellValueChanged", onCellValueChanged);
    props.api.addEventListener("filterChanged", onFilterChanged);

    return () => {
      props.api.removeEventListener("cellValueChanged", onCellValueChanged);
      props.api.removeEventListener("filterChanged", onFilterChanged);
    };
  }, [props.api, updateHeaderCheckboxState]);

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <label className="ag-checkbox" style={{ margin: 0 }}>
        <Checkbox
          className="ag-checkbox-input"
          checked={checked}
          onChange={onChange}
          onTouchEnd={(e) => {
            handleToggle(!checked);
          }}
          sx={{
            height: "30px",
            "& .MuiSvgIcon-root": {
              fontSize: 22,
            },
            alignContent: "center",
            color: "#7d7e80",
          }}
        />
      </label>
    </div>
  );
});

export const NumberBoxCellEditor = memo(
  ({ value, onValueChange, stopEditing, api }: CustomCellEditorProps) => {
    const ref = useRef<HTMLInputElement>(null);

    useEffect(() => {
      ref.current?.focus();
      ref.current?.select();
    }, []);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      console.log("NumberBoxCellEditor - handleKeyDown", e.key);
      if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();

        // 값 커밋
        onValueChange(ref.current?.value ? Number(ref.current.value) : null);

        // 편집 종료 (commit)
        stopEditing();

        // 다음 셀로 이동 — 단, '편집 모드로 진입'은 막음
        if (api) {
          api.tabToNextCell();
        }
      } else if (e.key === "Escape") {
        stopEditing(true); // 취소
      }
    };

    const handleBlur = () => {
      onValueChange(ref.current?.value ? Number(ref.current.value) : null);
      stopEditing();
    };

    return (
      <input
        ref={ref}
        type="number"
        defaultValue={value ?? ""}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        style={{ width: "100%", textAlign: "right" }}
        className="ag-input-field-input ag-text-field-input"
      />
    );
  }
);

export const PassWordCellEditor = memo(
  ({ value, onValueChange, eventKey, column }: CustomCellEditorProps) => {
    const updateValue = (val: string) => {
      onValueChange(val === "" ? null : val);
    };

    useEffect(() => {
      let startValue;

      if (eventKey === "Backspace") {
        startValue = "";
      } else if (eventKey && eventKey.length === 1) {
        startValue = eventKey;
      } else {
        startValue = value;
      }
      if (startValue == null) {
        startValue = "";
      }

      updateValue(startValue);

      refInput.current?.focus();
    }, []);

    const refInput = useRef<HTMLInputElement>(null);

    return (
      <input
        type="password"
        value={value || ""}
        ref={refInput}
        onChange={(event) => updateValue(event.target.value)}
        className="my-simple-editor"
      />
    );
  }
);

export const MaskCellEditor = memo(
  ({
    value,
    onValueChange,
    stopEditing,
    data,
    column,
  }: CustomCellEditorProps) => {
    const [format, setFormat] = useState(""); // 달력 열림 상태
    useEffect(() => {
      const colDef = column.getColDef() as any;

      if (colDef.columnConfig) {
        setFormat(colDef.columnConfig.tp_format);
      }
    }, []);

    return (
      <PatternFormat
        value={value}
        customInput={TextField}
        format={format}
        mask="_"
        onValueChange={(values) => {
          onValueChange(values.value);
        }}
        onBlur={() => stopEditing()}
        fullWidth
        autoFocus
        sx={{
          "& .MuiInputBase-root": {
            height: "32px",
            fontSize: "12px",
          },
        }}
      />
    );
  }
);
